import api from '../bu/api';
import moment from 'moment';
import { calculateSubtotal, dateMatchesPricingRule } from '../bu/RentableUtils';

export default {
	namespaced: true,

	state: () => ({
		providerTimezone: null,
		items: [],
		pointsDeduction: null,
		logistic: null, // logistic object
		logisticRule: null, // logistic rule object
		logisticTotal: null,
		address: {
			country: null,
			address: null,
			postal_code: null,
			admin_level_1: null,
			admin_level_2: null,
			admin_level_3: null
		}, // address object
		paymentMethodId: null,
		receiverName: null,
		receiverPhone: null,
		start: null, end: null, // booking start and end for calculation of renting items
		preventEmpty: false,
		groupAmountLeftInfo: [],
		pointsDeductionTotal: null
	}),

	getters: {
		start(state) {
			return state.start;
		},
		end(state) {
			return state.end;
		},
		groupAmountLeftInfo(state) {
			return state.groupAmountLeftInfo;
		},
		preventEmpty(state) {
			return state.preventEmpty;
		},
		receiverName(state) {
			return state.receiverName
		},
		receiverPhone(state) {
			return state.receiverPhone
		},
		paymentMethodId(state) {
			return state.paymentMethodId;
		},
		logistic(state) {
			return state.logistic;
		},
		logisticRule(state) {
			return state.logisticRule;
		},
		logisticTotal(state, getter) {
			if (getter.eligibleForFreeShipping) {
				return 0;
			}
			return state.logisticTotal
		},
		address(state) {
			return state.address;
		},
		items(state) {
			return state.items;
		},
		pointsDeduction(state) {
			return state.pointsDeduction;
		},
		isCartEmpty(state) {
			return state.items.filter((item) => (item.itemId || item.specId || item.serviceId || item.booking_pass_id) && item.count > 0).length == 0
		},
		isCartLogisticNeeded(state, _, rootStore) {
			const items = state.items.filter(item => item.itemId || item.specId);
			const itemsNoNeedLogistic = items.filter((cartItem) => {
				let buyable;
				if (cartItem.specId) {
					buyable = rootStore.items.specs[cartItem.itemId]?.find((s) => s.id === cartItem.specId);
				} else {
					buyable = rootStore.items.items[cartItem.itemId];
				}
				return buyable?.logisticIds.length === 0;
			});
			return itemsNoNeedLogistic.length  !== items.length;
		},
		eligibleForFreeShipping(_, getter) {
			const logistic = getter.logistic;

			if (!logistic) {
				return false;
			}

			if (logistic.free_shipping_for_order_amount) {
				if ((getter.itemTotal - getter.offerTotal - getter.pointsDeductionTotal) >= logistic.free_shipping_for_order_amount) {
					return true;
				}
			}
			if (logistic.free_shipping_for_count && getter.totalItemCountForLogistic >= logistic.free_shipping_for_count) {
				return true;
			}
			return false;
		},
		totalItemCountForLogistic(state, _, rootStore) {
			return state.items.filter((cartItem) => {
				if (!cartItem.itemId && !cartItem.specId) {
					return false;
				}
				let item = rootStore.items.items[cartItem.itemId];
				return item.should_count_for_free_logistic_rule && item.logisticIds.length > 0;
			})
			.reduce((count, cartItem) => {
				return +cartItem.count + count;
			}, 0);
		},
		totalItemCount(state, _, rootStore) {
			return state.items.filter(item => item.itemId || item.specId).reduce((count, cartItem) => {
				return +cartItem.count + count;
			}, 0);
		},
		itemTotal(state, _, rootStore) {
			return state.items.filter(item => item.itemId || item.specId).reduce((total, cartItem) => {
				if (cartItem.isSplitted || cartItem.appliedPricingRule) {
					return total + cartItem.total;
				}

				var buyable;
				var item = rootStore.items.items[cartItem.itemId];
				const isRenting = cartItem.is_renting || item.is_renting;
				if (cartItem.specId) {
					buyable = rootStore.items.specs[cartItem.itemId].find((s) => s.id === cartItem.specId);
				} else {
					buyable = rootStore.items.items[cartItem.itemId];
				}

				var itemTotal = 0;
				if (isRenting) {
					if (state.start && state.end) {
						if (cartItem.isSplitted) {
							itemTotal += cartItem.renting_charge_amount * cartItem.count;
						} else {
							const startMoment = moment(state.start);
							const endMoment = moment(state.end);
							itemTotal = calculateSubtotal(buyable, cartItem.count, startMoment, endMoment);
						}
					}
				} else {
					itemTotal = parseInt(buyable.price) * cartItem.count;
				}
				return total + itemTotal;
			}, 0);
		},
		pointsDeductableItemTotal(state, _, rootStore) {
			return state.items.filter(item => item.pointsDeductable).reduce((total, cartItem) => {
				var itemTotal = 0;

				if (cartItem.serviceId) {
					itemTotal = cartItem.total;
				} else {
					var buyable;
					var item = rootStore.items.items[cartItem.itemId];
					const isRenting = item.is_renting;
					if (cartItem.specId) {
						buyable = rootStore.items.specs[cartItem.itemId].find((s) => s.id === cartItem.specId);
					} else {
						buyable = rootStore.items.items[cartItem.itemId];
					}

					if (isRenting) {
						if (state.start && state.end) {
							const startMoment = moment(state.start).startOf('day');
							const endMoment = moment(state.end).startOf('day');
							itemTotal = calculateSubtotal(buyable, cartItem.count, startMoment, endMoment);
						}
					} else {
						itemTotal = parseInt(buyable.price) * cartItem.count;
					}
				}
				return total + itemTotal;
			}, 0);
		},
		offerTotal(state) {
			return state.items.filter(item => item.offer_id || item.campaign_id).reduce((total, cartItem) => {
				if (isNaN(cartItem.total)) {
					return total;
				} else {
					return total + +cartItem.total;
				}
			}, 0);
		},
		serviceItem(state) {
			return state.items.find(item => item.serviceId);
		},
		serviceTotal(state) {
			const serviceItem = state.items.find(item => item.serviceId);
			if (serviceItem) {
				return serviceItem.total;
			} else {
				return 0;
			}
		},
		bookingPassItem(state) {
			return state.items.find(item => item.booking_pass_id);
		},
		bookingPassTotal(state) {
			const bookingPassItem = state.items.find(item => item.booking_pass_id);
			return bookingPassItem ? bookingPassItem.total : 0;
		},
		pointsDeductionTotal(state) {
			return state.pointsDeductionTotal;
		},
		hasPresetInBookingPass(state) {
			const bookingPassItem = state.items.find(item => item.booking_pass_id);
			if (bookingPassItem) {
				return bookingPassItem.bookingPass.presets.length > 0;
			}
			return false;
		}
	},

	actions: {
		setProviderTimezone({commit}, timezone) {
			commit('setProviderTimezone', timezone);
		},
		setPreventEmpty({commit}, preventEmpty) {
			commit('setPreventEmpty', preventEmpty);
		},
		reset({commit}) {
			commit('reset')
		},
		setReceiverName({commit}, name) {
			commit('setReceiverName', name);
		},
		setReceiverPhone({commit}, phone) {
			commit('setReceiverPhone', phone);
		},
		setItem({commit, dispatch}, {item, spec, count, questionId}) {
			commit('addItem', {item, spec, count, questionId});
			dispatch('applyPricingRulesIfNeeded');
		},
		removeItem({commit, dispatch}, {item, spec}) {
			commit('removeItem', {item, spec});
			dispatch('applyPricingRulesIfNeeded');
		},
		setServiceItem({commit, dispatch, state}, {service}) {
			commit('addServiceItem', {service});
			dispatch('applyPricingRulesIfNeeded');
		},
		removeServiceItem({commit, dispatch}) {
			commit('removeServiceItem');
			dispatch('applyPricingRulesIfNeeded');
		},
		setCampaigns({commit}, {campaigns}) {
			commit('addCampaigns', {campaigns});
		},
		setOffer({commit}, {offer, customerOfferId}) {
			commit('addOffer', {offer, customerOfferId});
		},
		removeOffer({commit}, offer) {
			commit('removeOffer', offer);
		},
		setPointsDeduction({commit}, pointsDeduction) {
			commit('setPointsDeduction', pointsDeduction);
		},
		setPointsDeductionTotal({commit}, discount) {
			commit('setPointsDeductionTotal', discount);
		},
		setLogistic({commit}, logistic) {
			commit('setLogistic', logistic)
		},
		setLogisticRule({commit}, logisticRule) {
			commit('setLogisticRule', logisticRule)
		},
		setAddress({commit}, address) {
			commit('setAddress', address)
		},
		setPaymentMethodId({commit}, id) {
			commit('setPaymentMethodId', id);
		},
		setStart({commit, dispatch}, start) {
			commit('setStart', start);
			dispatch('applyPricingRulesIfNeeded');
		},
		setEnd({commit, dispatch}, end) {
			commit('setEnd', end);
			dispatch('applyPricingRulesIfNeeded');
		},
		removeCartItemByItemIds({commit, dispatch}, ids) {
			for(var i in ids) {
				commit('removeItemByItemId', ids[i]);
			}
			dispatch('applyPricingRulesIfNeeded');
		},
		removeCartItem({commit, dispatch}, cartItem) {
			commit('removeCartItem', cartItem);
			dispatch('applyPricingRulesIfNeeded');
		},
		setCartLogisticTotal({commit}, logisticTotal) {
			commit('setCartLogisticTotal', logisticTotal);
		},
		async getFormBuyableAdvancedCap({commit, state}, {serviceId, start, end}) {
			const response = await api.getFormBuyableAdvancedCap(serviceId, start, end);
			commit('setGroupAmountLeftInfo', response.data.capped_groups);
		},
		applyPricingRulesIfNeeded({commit, state, rootState}) {
			let itemModuleState = rootState.items;
			console.debug('Start applying pricing rules');
			let resultItems = [];

			console.debug('Merging item/spec items back in case there are items being splitted already by the pricing rules');
			var mergedBackItems = [];
			for(var i in state.items) {
				const item = state.items[i];
				if (item.specId) {
					let existing = mergedBackItems.find((existingItem) => existingItem.specId == item.specId);
					if (!existing) {
						mergedBackItems.push(item);
					}
				} else if (item.itemId) {
					let existing = mergedBackItems.find((existingItem) => existingItem.itemId == item.itemId);
					if (!existing) {
						mergedBackItems.push(item);
					}
				} else if (!item.campaignId) {
					// service item can pass to result directly
					resultItems.push(item);
				}
			}

			console.debug('Checking item/spec pricing rules');
			for(var i in mergedBackItems) {
				const cartItem = mergedBackItems[i];
				let pricingRules = null; 
				var item, spec, target;
				if (cartItem.specId) {
					item = itemModuleState.items[cartItem.itemId];
					spec = itemModuleState.specs[cartItem.itemId]?.find((s) => s.id == cartItem.specId);
					target = spec;
					pricingRules = target?.pricing_rules;
				} else {
					item = itemModuleState.items[cartItem.itemId];
					target = item;
					pricingRules = target?.pricing_rules;
				}

				if (!pricingRules || pricingRules.length === 0) {
					console.debug('No pricing rules, pass directly', [target]);
					resultItems.push(cartItem);
				} else {
					console.debug('Pricing rules found. Checking...');
					if (!state.start || !state.end) {
						if (item.is_renting || !state.start) {
							console.debug('Set to no pricing and total given no start/end selected');
							cartItem.failToCalculatePrice = true;
							resultItems.push(cartItem);
						} else {
							cartItem.failToCalculatePrice = false;
							let startMoment = moment.tz(state.start, state.providerTimezone);
							let rule = dateMatchesPricingRule(startMoment, pricingRules);
							if (rule) {
								cartItem.price = rule.price;
								cartItem.appliedPricingRule = rule;
							} else {
								cartItem.price = +target.price;
							}
							cartItem.total = cartItem.price * cartItem.count;
							resultItems.push(cartItem);
						}
					} else {
						cartItem.failToCalculatePrice = false;
						let startMoment = moment.tz(state.start, state.providerTimezone);
						if (item.is_renting) {
							// from start to end, loop every day and check if it matches pricing rules
							let endMoment = moment.tz(state.end, state.providerTimezone);
							const chargeType = target.renting_charge_type;
							cartItem.is_renting = true;
							cartItem.renting_charge_type = chargeType;
							cartItem.renting_charge_amount = target.renting_charge_amount;

							if (chargeType === 'BOOKING') {
								// If there's a day match a pricing rule just apply it directly
								while(startMoment.isSameOrBefore(endMoment)) {
									let rule = dateMatchesPricingRule(startMoment, pricingRules);
									if (rule) {
										cartItem.total = rule.price * cartItem.count;
										cartItem.renting_charge_amount = rule.price;
										cartItem.appliedPricingRule = rule;
										break;
									}
									startMoment.add(1, 'day');
								}
								resultItems.push(cartItem);
							} else if (chargeType === 'DAY' || chargeType === 'NIGHT') {

								const bookingStartMoment = moment(startMoment);
								while(startMoment.isSameOrBefore(endMoment)) {
									console.debug('Step to date', startMoment);

									if (chargeType === 'NIGHT' && startMoment.clone().endOf('day').isSame(endMoment.clone().endOf('day'))) {
										// If it's charged by NIGHT and start/end has already on the same day means we should stop counting 
										break;
									}

									let rule = dateMatchesPricingRule(startMoment, pricingRules);
									let splittedCartItem = Object.assign({}, cartItem);
									if (rule) {
										splittedCartItem.renting_charge_amount = rule.price;
										splittedCartItem.appliedPricingRule = rule;
									} else {
										splittedCartItem.renting_charge_amount = target.renting_charge_amount;
										splittedCartItem.appliedPricingRule = null;
									}
									if (chargeType === 'DAY') {
										splittedCartItem.dateRange = {
											startMoment: moment(startMoment).startOf('day'), endMoment: moment(startMoment).endOf('day')
										};
										if (startMoment.isSame(bookingStartMoment)) {
											splittedCartItem.dateRange.startMoment = moment(startMoment);
										}
										if (startMoment.isSame(endMoment)) {
											splittedCartItem.dateRange.endMoment = moment(endMoment);
										}
									} else if (chargeType === 'NIGHT') {
										if (moment(startMoment).add(1, 'day').endOf('day').isSame(moment(endMoment).endOf('day'))) {
											splittedCartItem.dateRange = {
												startMoment: moment(startMoment),
												endMoment: moment(endMoment)
											};
										} else {
											splittedCartItem.dateRange = {
												startMoment: moment(startMoment), endMoment: moment(startMoment).add(1, 'day')
											};
										}
									}
									splittedCartItem.isSplitted = true; // which implicitly means billableUnit = 1
									splittedCartItem.total = splittedCartItem.renting_charge_amount * splittedCartItem.count;
									console.debug("Splitted Cart item appended", splittedCartItem);

									resultItems.push(splittedCartItem);

									startMoment.add(1, 'day');
								}

							} else if (chargeType === 'HOUR') {
								// Todo: skip for now, just don't apply pricing rules
								resultItems.push(cartItem);
							}
						} else {
							let rule = dateMatchesPricingRule(startMoment, pricingRules);
							if (rule) {
								cartItem.price = rule.price;
								cartItem.appliedPricingRule = rule;
							} else {
								cartItem.price = +target.price;
							}
							cartItem.total = cartItem.price * cartItem.count;
							resultItems.push(cartItem);
						}
					}
				}
			}

			console.debug('Reapply offers');

			console.debug('Reapply campaigns');

			console.debug('Fully Reset Items', resultItems);
			commit('fullyResetItems', resultItems);
		},
		resetCartLogistic({commit}) {
			commit('resetCartLogistic');
		},
		addBookingPass({ state, commit }, bookingPass) {
			const noBookingPassYet = state.items.filter(item => item.booking_pass_id).length  === 0;
			if (noBookingPassYet) {
				const itemsWithoutBookingPass = state.items.filter(item => {
					const preset = bookingPass.presets.length > 0 ? bookingPass.presets[0] : null;
					if (preset && preset.provider_item_id == item.itemId) {
						return false;
					}
					if (preset && preset.provider_item_spec_id == item.specId) {
						return false;
					}
					return !item.booking_pass_id;
				});
				itemsWithoutBookingPass.push({
					booking_pass_id: bookingPass.id,
					count: 1,
					price: bookingPass.price,
					total: bookingPass.price,
					bookingPass
				});
				commit('setItems', itemsWithoutBookingPass);
			}
		},
		removeBookingPass({ state, commit }) {
			const items = state.items.filter((item) => !item.booking_pass_id);
			commit('setItems', items);
		},
		storeAllDataToLocalStorage({ state }) {
			const expireAt = moment().add(3, 'minute').format();
			const cart = {
				...state,
				expire_at: expireAt,
			}
			localStorage.setItem('cart', JSON.stringify(cart));
		},
		loadDataFromLocalStorage({ commit }) {
			commit('loadDataFromLocalStorage');
		},
	},

	mutations: {
		setProviderTimezone(state, timezone) {
			state.providerTimezone = timezone;
		},
		setItems(state, newItems) {
			state.items = newItems;
		},
		setGroupAmountLeftInfo(state, groupAmountLeftInfo) {
			state.groupAmountLeftInfo = groupAmountLeftInfo;
		},
		setPreventEmpty(state, preventEmpty) {
			state.preventEmpty = preventEmpty;
		},
		setReceiverName(state, name) {
			state.receiverName = name;
		},
		setReceiverPhone(state, phone) {
			state.receiverPhone = phone;
		},
		reset(state) {
			state.items = [];
			state.logistic = null;
			state.logisticRule = null;
			state.address = {
				country: null,
				address: null,
				postal_code: null,
				admin_level_1: null,
				admin_level_2: null,
				admin_level_3: null
			};
			state.paymentMethodId = null;
		},
		setPaymentMethodId(state, paymentMethodId) {
			state.paymentMethodId = paymentMethodId;
		},
		setStart(state, start) {
			state.start = start;
		},
		setEnd(state, end) {
			state.end = end;
		},
		setLogistic(state, logistic) {
			state.logistic = logistic;
		},
		setLogisticRule(state, logisticRule) {
			state.logisticRule = logisticRule;
		},
		setAddress(state, address) {
			state.address = address;
		},
		fullyResetItems(state, items) {
			state.items = items;
		},
		addItem(state, {item, spec, count, questionId}) {
			var existing = null;
			if (spec) {
				existing = state.items.find((checkee) => { return spec.id == checkee.specId });
			} else {
				existing = state.items.find((checkee) => { return item.id == checkee.itemId });
			}

			if (count === 0 && existing) {
				state.items = state.items.filter((checkee) => checkee !== existing);
			} else if (count > 0) {
				const items = state.items;
				if (existing) {
					const index = items.indexOf(existing)
					items.splice(index, 1, Object.assign({}, existing, {count}));
				} else {
					items.push({
						itemId: item.id, 
						specId: (spec ? spec.id : null), 
						count, 
						questionId, 
						pointsDeductable: !!item.points_deductable
					});
				}
				state.items = items;
			}
		},
		removeItem(state, {item, spec}) {
			const existing = state.items.find((checkee) => {
				return checkee.itemId == item.id && (spec && spec.id == checkee.specId)
			});
			if (existing) {
				existing.count -= 1;
				if (existing.count === 0) {
					state.items = state.items.filter((checkee) => checkee !== existing);
				}
			}
		},
		removeItemByItemId(state, itemId) {
			state.items = state.items.filter((item) => {
				return item.itemId != itemId;
			});
		},
		removeItemBySpecId(state, specId) {
			state.items = state.items.filter((item) => {
				return item.specId != specId;
			});
		},
		removeCartItem(state, cartItem) {
			state.items = state.items.filter((item) => {
				return item != cartItem;
			});
		},
		setCartLogisticTotal(state, logisticTotal) {
			state.logisticTotal = logisticTotal;
		},
		addCampaigns(state, {campaigns}) {
			const items = state.items.filter(item => !item.campaign_id);
			campaigns.forEach(campaign => {
				items.push({
					campaign_id: campaign.id,
					count: 1,
					price: campaign.discount,
					total: campaign.discount,
					campaign: campaign
				});
			})
			state.items = items;
		},
		addOffer(state, {offer, customerOfferId}) {
			const items = state.items.filter(item => !item.offer_id);
			if (offer.discount !== 0) {
				items.push({
					offer_id: offer.id,
					count: 1,
					price: offer.discount,
					total: offer.discount,
					offer: offer,
					customer_offer_id: customerOfferId
				});
			}
			state.items = items;
		},
		removeOffer(state, offer) {
			state.items = state.items.filter((checkee) => {
				if (checkee.offer_id) {
					return checkee.offer_id !== offer.id
				} else {
					return true;
				}
			});
		},
		addServiceItem(state, {service}) {
			const items = state.items.filter(item => !item.serviceId);

			let price = 0;
			if (service.auto_request_payment) {
				price = service.price_min;
			} else if (service.auto_request_down_payment) {
				price = service.down_payment_amount;
			}
			items.push({
				serviceId: service.id,
				count: 1,
				price,
				total: price,
				isDownPayment: service.auto_request_down_payment,
				pointsDeductable: service.points_deductable
			});
			state.items = items;
		},
		removeServiceItem(state) {
			state.items = state.items.filter(item => !item.serviceId);
		},
		setPointsDeduction(state, pointsDeduction) {
			state.pointsDeduction = pointsDeduction;
		},
		setPointsDeductionTotal(state, discount) {
			state.pointsDeductionTotal = discount;
		},
		resetCartLogistic(state) {
			state.logistic = null;
			state.logisticRule = null;
			state.logisticTotal = null;
			state.address = {
				country: null,
				address: null,
				postal_code: null,
				admin_level_1: null,
				admin_level_2: null,
				admin_level_3: null
			};
			state.receiverName = null;
			state.receiverPhone = null;
		},
		loadDataFromLocalStorage(state) {
			const cart = JSON.parse(localStorage.getItem('cart'));
			if (cart) {
				const { expire_at } = cart;
				if (expire_at && moment().isBefore(expire_at)) {
					const keys = Object.keys(cart);
					keys.forEach((key) => {
						if (key !== 'expire_at') {
							state[key] = cart[key];
						}
					});
				}
				localStorage.removeItem('cart');
			}
		},
	},
}
